<template>
  <hr-page>
    <breadcrumbs :custom-page-label="$t('settings.offlineAccess.breadcrumbOfflineAccessLabel')" />
    <div class="q-pa-none">
      <records-table
        v-model:selected="selectedClients"
        :title="$t('clients.breadcrumbElLabel')"
        :description="$t('settings.offlineAccess.textCaption')"
        :default-view-label="$t('clients.defaultViewLabel')"
        :search-placeholder="$t('clients.inputSearch')"
        :rows="clientsOnly"
        :columns="clientsColumns"
        :column-group-labels="clientsColumnGroups"
        :default-columns="['clientName', 'units', 'status']"
        :loading="isLoadingClients"
        :default-filters="defaultFilters"
        :pagination="initialPagination"
        :selection="offlineMode ? 'none' : 'multiple'"
        picker
        :row-label="(client) => client.clientName ?? ''"
        :disabled-rows="clientsOverTheCap"
        :disabled-row-tooltip="$t('settings.offlineAccess.checkboxDisabled')"
        save-filter-id="offlineClients"
        standalone
        disable-export
      >
        <!--
          No select-all: it skips disabled rows, and with nothing chosen yet no row is over the cap,
          so one click would take every client and overshoot it.
        -->
        <template #header-selection />
        <template #empty-filtered="{ hasSearchTerm, hasFilters, clearSearch, clearFilters }">
          <hr-empty-state
            icon="search_off"
            :title="$t('clients.emptyFilteredTitle')"
            :message="$t('clients.emptyFilteredMessage')"
          >
            <template #actions>
              <hr-supporting-btn
                v-if="hasSearchTerm"
                :label="$t('common.buttonClearSearch')"
                data-e2e="button-clear-search"
                @click="clearSearch"
              />
              <hr-subtle-btn
                v-if="hasFilters"
                :label="$t('common.buttonClearFilters')"
                data-e2e="button-clear-filters"
                @click="clearFilters"
              />
            </template>
          </hr-empty-state>
        </template>
      </records-table>
      <q-form @submit="onSaveClients">
        <q-card-actions align="between">
          <hr-subtle-btn
            :disable="!networkOnline || offlineMode"
            :label="$t('common.refreshOfflineData')"
            data-e2e="button-refresh-offline-data"
            @click="refreshOfflineData"
          />
          <div class="row q-gutter-sm q-ml-auto">
            <hr-dismiss-btn
              :label="$t('common.buttonCancel')"
              @click="onCancelClick"
            />
            <hr-confirm-btn
              type="submit"
              :disable="!networkOnline || offlineMode"
              :loading="waitingSpinner"
              :label="$t('common.buttonSave')"
              data-e2e="button-save"
            />
          </div>
        </q-card-actions>
      </q-form>
    </div>
  </hr-page>
</template>
<script>

import { filterPropertyTypes } from 'boot/globals';
import { isIndexedDbOpened } from 'boot/indexedDb';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import {
  HrConfirmBtn,
  HrDismissBtn,
  HrEmptyState,
  HrPage,
  HrSubtleBtn,
  HrSupportingBtn,
} from 'src/design-system';
import EncryptionKeysDialog from 'components/Modals/EncryptionKeysDialog';
import RecordsTable from 'components/UI/RecordsTable.vue';
import { chain, get, isEqual, map } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { uid } from 'quasar';
import Auth from 'src/mixins/Auth';
import ClientsMixin from 'src/mixins/ClientsMixin';
import Integrations from 'src/mixins/Integrations';
import Interactions from 'src/mixins/Interactions';
import { auth } from 'src/store/auth';
import { clients } from 'src/store/clients';
import { network } from 'src/store/network';
import { units } from 'src/store/units';
import { readSecret } from 'src/utils/CryptoUtils';
import { logEvent } from 'src/utils/LogEvent';
import { syncToOffline } from 'src/utils/OfflineAccessUtils';

/*
 * How many clients may be taken offline at once. The device holds a full copy of each, so this is a
 * storage limit rather than a policy one.
 */
const OFFLINE_CLIENT_LIMIT = 5;

export default {
  name: 'OfflineAccess',

  components: {
    Breadcrumbs,
    HrConfirmBtn,
    HrDismissBtn,
    HrEmptyState,
    HrPage,
    HrSubtleBtn,
    HrSupportingBtn,
    RecordsTable,
  },

  mixins: [ClientsMixin, Integrations, Interactions, Auth],

  data() {
    return {
      clientsFilter: '',
      selectedClients: [],
      waitingSpinner: false,
      initialPagination: {
        page: 1,
        rowsPerPage: 15,
      },
    };
  },

  computed: {
    ...mapState(auth, ['userSettings', 'connectedClinic', 'userDetails']),
    ...mapState(clients, ['isLoadingClients', 'clients']),
    ...mapState(network, ['networkOnline', 'offlineMode']),

    /*
     * Everything still unselected, once the cap is reached. Modelled as disabled rows rather than a
     * checkbox the page draws itself, so the cap gets the table's own tooltip and stops being a
     * second implementation of what a row may do.
     */
    clientsOverTheCap() {
      if (this.selectedClients.length < OFFLINE_CLIENT_LIMIT) {
        return [];
      }
      const chosen = new Set(this.selectedClients.map((client) => client.id));
      return this.clientsOnly.filter((client) => !chosen.has(client.id));
    },

    clientsOnly() {
      const clientsOnly = this.clients.filter((client) => client.type !== 'staff');

      if (this.offlineMode) {
        return clientsOnly.filter((client) => this.userSettings?.offlineClients?.includes(client.id));
      }

      return clientsOnly;
    },

    clientsColumns() {
      return chain(this.clientColumns)
        .filter((column) => (chain(column).get('profileTypes').includes('client').value()))
        .concat(
          map(
            this.clientDataColumns,
            (clientDataColumn) => ({
              ...clientDataColumn,
              group: 'clientData',
            }),
          ),
        )
        .value();
    }, // clientsColumns()

    clientsColumnGroups() {
      return [
        {
          group: 'clientData',
          label: this.$t('clients.columnGroupClientData'),
        },
        {
          group: 'integration',
          label: this.$t('clients.columnGroupIntegration', {
            integration: this.getIntegrationName(get(this.connectedClinic, 'integration')),
          }),
        },
      ];
    }, // clientsColumnGroups()

    defaultFilters() {
      return {
        status: {
          id: uid(),
          filterProperty: 'status',
          filterPropertyType: filterPropertyTypes.StringInArray,
          filterStatements: [
            {
              filterDateItems: [],
              fromDate: null,
              toDate: null,
              customDataId: null,
              filterCriteriaType: null,
              filterNegative: false,
              filteredItems: ['active'],
              label: `${this.$t('clients.columnsStatusLabel')}: ${this.$t('clients.status.active')}`,
            },
          ],
        },
      };
    }, // defaultFilters()

  },

  watch: {
    isLoadingClients() {
      this.setDefaultSelection();
    },
  },

  async mounted() {
    if (!this.userHasPrivilege('offline/hasAccess')) {
      this.$router.push({ name: 'Home' });
    }

    this.setDefaultSelection();

    await Promise.all([
      this.loadClinicUnits(),
      this.syncClients(),
    ]);
  },

  methods: {
    ...mapActions(auth, ['changeCurrentClient', 'updateUserSettings']),
    ...mapActions(units, ['loadClinicUnits']),
    ...mapActions(clients, ['syncClients']),

    onCancelClick() {
      this.selectedClients = [];
    },

    async onSaveClients() {
      if (!isIndexedDbOpened()) {
        return this.alertDialog(
          this.$t('modals.offlineModal.unsupportedTitle'),
          this.$t('modals.offlineModal.unsupportedMessage'),
        );
      }

      const offlineClientsIds = this.selectedClients.map(({ id }) => id);

      if (isEqual(this.userSettings.offlineClients, offlineClientsIds)) {
        return;
      }

      const checkKey = await readSecret('publicKey');

      if (checkKey || offlineClientsIds.length === 0) {
        return this.saveOfflineClients(offlineClientsIds);
      }

      this.openComponentDialog(EncryptionKeysDialog, {
        skipSync: true,
      }, async () => {
        await this.saveOfflineClients(offlineClientsIds);
      });
    }, // onSaveClients()

    async saveOfflineClients(offlineClientsIds) {
      try {
        this.waitingSpinner = true;

        await this.updateUserSettings({ offlineClients: offlineClientsIds });
        await syncToOffline({ resetDb: true });

        this.notifySuccess(this.$t('settings.offlineAccess.saveSuccess'));
      } catch (error) {
        logEvent(error);
        console.error('Error updating client', error);
        this.notifyNegative(this.$t('modals.offlineModal.syncingToOfflineError', { message: error.message }));
      } finally {
        this.waitingSpinner = false;
      }
    }, // saveOfflineClients()

    async refreshOfflineData() {
      if (!isIndexedDbOpened()) {
        return this.alertDialog(
          this.$t('modals.offlineModal.unsupportedTitle'),
          this.$t('modals.offlineModal.unsupportedMessage'),
        );
      }

      return syncToOffline({ resetDb: true })
        .then(() => {
          this.notifySuccess(this.$t('settings.offlineAccess.saveSuccess'));
        })
        .catch((err) => {
          logEvent(err);
          console.error('Error refreshing offline data', err);
          this.notifyNegative(this.$t('modals.offlineModal.syncingToOfflineError', { message: err.message }));
        });
    },

    /*
     * Clamped on the way in. The cap disables rows nobody has chosen yet, which does nothing about
     * a stored list that is already over it — those rows arrive selected, so nothing stops them
     * being saved straight back.
     */
    setDefaultSelection() {
      if (!this.isLoadingClients) {
        this.selectedClients = this.clientsOnly
          .filter((client) => this.userSettings?.offlineClients?.includes(client.id))
          .slice(0, OFFLINE_CLIENT_LIMIT);
      }
    },
  },

};
</script>
